import { Button, Input } from "@wac-ui-dashboard/wac_component_library";
import useAddUsers from "./useAddUser";
import Style from "../subscriptionAccess.module.scss";

const AddUsers = ({ subscriptionAccessState, setSubscriptionAccessState, refetch }) => {
  const {
    formik,
    handleCloseModal,
    getFieldError,
    handleAddSelectedUser,
    handleRemoveSelectedUser,
  } = useAddUsers({
    subscriptionAccessState,
    setSubscriptionAccessState,
    refetch,
  });

  const unregisteredMobiles =
    subscriptionAccessState?.itemData?.unregistered_mobiles ?? [];

  return (
    <div className="row">
      {/*
       * IMPORTANT: this allowlist controls whether the subscription feature
       * is visible in the mobile app at all.
       *
       * - An EMPTY list means NOBODY can see the subscription feature.
       *   (This is the opposite of the AI Settings screen, where empty = everybody.)
       * - Setting Status to Deactivated closes the module to everyone regardless
       *   of what is in the list.
       * - Add a number here to give that customer visibility. They gain access
       *   automatically when they register — you do not need to re-save.
       */}
      <div className="col-12 pro-mb-4">
        <div
          style={{
            background: "var(--pro-secondary-hover)",
            borderLeft: "3px solid var(--pro-primary)",
            borderRadius: "3px",
            padding: "8px 12px",
            fontSize: "0.85rem",
          }}
        >
          <p className="pro-mb-1">
            <strong>Important:</strong> this setting controls who can see the
            subscription feature in the mobile app.
          </p>
          <ul className="pro-mb-0" style={{ paddingLeft: "1.2rem" }}>
            <li>
              <strong>Deactivated</strong> — nobody sees subscriptions,
              regardless of any other setting.
            </li>
            <li>
              <strong>Active + "Only the numbers below"</strong> — only the
              listed numbers have access; an empty list means nobody sees
              subscriptions.
            </li>
            <li>
              <strong>Active + "Everyone"</strong> —{" "}
              <strong>all app users</strong> gain access to subscriptions.
              Use with caution: this publishes the subscription feature to
              your entire user base.
            </li>
          </ul>
        </div>
      </div>

      <>
        <div className="col-12 pro-mb-3">
          <label className="pro-font-sm pro-mb-1 pro-fw-medium">
            Who has access?
          </label>
          <div className="pro-d-flex pro-gap-4">
            <div className="pro-check-box">
              <input
                type="radio"
                className="pro-check"
                id="eligibility_mobile_list"
                name="eligibility_type"
                checked={formik.values.eligibility_type === "mobile_list"}
                onChange={() =>
                  formik.setFieldValue("eligibility_type", "mobile_list")
                }
              />
              <label
                htmlFor="eligibility_mobile_list"
                className="pro-check-label"
              >
                Only the numbers below
              </label>
            </div>
            <div className="pro-check-box">
              <input
                type="radio"
                className="pro-check"
                id="eligibility_open"
                name="eligibility_type"
                checked={formik.values.eligibility_type === "open"}
                onChange={() =>
                  formik.setFieldValue("eligibility_type", "open")
                }
              />
              <label htmlFor="eligibility_open" className="pro-check-label">
                Everyone
              </label>
            </div>
          </div>
        </div>

        {formik.values.eligibility_type === "open" && (
          <div className="col-12 pro-mb-2">
            <p
              className="pro-mb-0"
              style={{
                fontSize: "0.82rem",
                color: "var(--pro-muted, #888)",
                fontStyle: "italic",
              }}
            >
              The list below is ignored while "Everyone" is selected. It is
              preserved so you can switch back to a pilot without re-entering
              numbers.
            </p>
          </div>
        )}

        <div className={Style.add_wrap}>
          <Input
            label={"Add Mobile Number"}
            type="text"
            id="add_user"
            name="add_user"
            className={`pro-input lg ${getFieldError("add_user") && "error"}`}
            {...formik.getFieldProps("add_user")}
            onChange={(e) => formik.setFieldValue("add_user", e.target.value)}
            error={getFieldError("add_user")}
            errorMessage={getFieldError("add_user")}
          />
          <button
            onClick={handleAddSelectedUser}
            className={`${Style.add_btn}`}
          >
            <span className="material-symbols-outlined x4"> add </span>
          </button>
        </div>
        <div>
          <div className={`${Style.number_badge_wrap} pro-mb-4`}>
            {formik?.values?.user_mobiles?.map((item, index) => (
              <div key={index} className={Style.badge}>
                {item}
                <span
                  onClick={() => handleRemoveSelectedUser(index)}
                  className="material-symbols-outlined"
                >
                  close
                </span>
              </div>
            ))}
          </div>
        </div>
        {formik?.errors?.user_mobiles &&
          formik?.values?.user_mobiles?.length <= 0 && (
            <span className="error-text error error-img">
              {getFieldError("user_mobiles")}
            </span>
          )}

        {unregisteredMobiles.length > 0 && (
          <div className="col-12 pro-mb-3">
            <div
              style={{
                background: "var(--pro-secondary-hover)",
                borderLeft: "3px solid var(--pro-warning, #f0a500)",
                borderRadius: "3px",
                padding: "8px 12px",
                fontSize: "0.85rem",
              }}
            >
              <p className="pro-mb-0">
                <strong>
                  {unregisteredMobiles.length}{" "}
                  {unregisteredMobiles.length === 1 ? "number has" : "numbers have"} no
                  account yet:
                </strong>{" "}
                {unregisteredMobiles.join(", ")}. This is not an error — they
                will gain access automatically when they register.
              </p>
            </div>
          </div>
        )}
      </>

      <div className="col-6">
        <div className="pro-check-box pro-py-2">
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              role="switch"
              id="status"
              onBlur={formik?.handleBlur}
              checked={formik.values.status === 1}
              onChange={(e) =>
                formik.setFieldValue(`status`, e.target.checked ? 1 : 2)
              }
            />
            <label className="form-check-label" htmlFor="status">
              Active (deactivating hides subscriptions for everyone)
            </label>
          </div>
          {getFieldError("status") && (
            <span className="error-text error error-img">
              {getFieldError("status")}
            </span>
          )}
        </div>
      </div>

      <div className="col-12 pro-d-flex pro-justify-end">
        <Button
          className="pro-btn-link lg pro-px-5"
          onClick={() => handleCloseModal()}
        >
          Cancel
        </Button>

        <Button
          className={`pro-btn-primary lg pro-ms-3 ${
            formik?.isSubmitting ? "loading" : ""
          }`}
          type="submit"
          onClick={formik.handleSubmit}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default AddUsers;
